import classNames from "classnames";
import type { ImageGravity } from "common/components/image-types";
import { ImageAspectRatio, Sizing } from "common/components/image-types";
import type { DropImageHandler } from "common/components/portable-renderers";
import { massageImageUrl } from "common/components/portable-renderers";
import { scaleImageWidthToHeight } from "../../../lib/image-utils";
import { disabledProps, styled } from "../../../styled-components";
import AutoSizer from "../../smart/autosizer/autosizer";
import Img from "../../smart/img/img";

const ImageStyle = styled(Img)`
    display: block;

    max-width: unset;

    &.cover {
        object-fit: cover;
    }

    &.contain {
        object-fit: contain;
    }

    &.disabled {
        ${disabledProps}
    }
`;

interface AutoImageProps extends React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> {
    readonly size?: ImageAspectRatio;
    readonly whenLoading?: React.ReactNode;
    readonly sizing?: Sizing;
    readonly gravity?: ImageGravity;
    readonly onDropImage?: DropImageHandler;
    readonly fontSize?: number;
    readonly disabled?: boolean;
}

const AutoImage: React.FunctionComponent<AutoImageProps> = p => {
    const { ref, size, sizing, gravity, whenLoading, fontSize, className, ...rest } = p;
    const cname = (sizing ?? "") + " " + className ?? "";
    const disabled = p.disabled ?? false;

    return (
        <div>
            <AutoSizer disableHeight={true}>
                {sizeProps => {
                    const width = sizeProps.width;
                    let height: number | undefined = sizeProps.height;
                    if (width !== undefined && size !== undefined && size !== ImageAspectRatio.Fit) {
                        height = scaleImageWidthToHeight(width, size);
                    }

                    let imageHeight: number | undefined = height;
                    if (sizing === Sizing.Contain) {
                        imageHeight = undefined;
                    }

                    const url = massageImageUrl(rest.src, {
                        width,
                        height: imageHeight,
                        gravity,
                        thumbnail: false,
                    });

                    const style = {
                        width,
                        height,
                        fontSize,
                    };

                    return (
                        <ImageStyle
                            {...rest}
                            className={classNames(cname, disabled ? "disabled" : undefined)}
                            style={style}
                            src={url}
                            alternate={whenLoading}
                            altBehavior="both"
                        />
                    );
                }}
            </AutoSizer>
        </div>
    );
};

export default AutoImage;
